import { flow, getRoot, types } from 'mobx-state-tree';

import Error from '../models/Error';
import StatefulStore from '../models/StatefulStore';
import {
  createEmptyWishlistDocument,
  createLegacyWishlistDocument,
  mergeWishlistItems,
  normalizeWishlistDocument,
  normalizeWishlistItem,
  WISHLIST_STORAGE_VERSION,
} from '../models/WishlistProduct';
import RequestState, { RequestStateType } from '../types/RequestState';
import { storageKeys } from '../util/constants';
import { createErrorModel } from '../util/error';
import {
  safeGetItem,
  safeRemoveItem,
  stringifyAndSetStorage,
} from '../util/storage';

const parseWishlistStorage = (key) => {
  const storage = safeGetItem(key);

  if (!storage || storage.trim() === '') {
    return null;
  }

  try {
    return JSON.parse(storage);
  } catch (_) {
    return null;
  }
};

const isCurrentWishlistDocument = (document) => {
  return (
    document &&
    typeof document === 'object' &&
    !Array.isArray(document) &&
    document.version === WISHLIST_STORAGE_VERSION
  );
};

const WishlistStore = StatefulStore.named('WishlistStore')
  .props({
    document: types.frozen(createEmptyWishlistDocument()),
    serverDocument: types.frozen(createEmptyWishlistDocument()),
    hydrateState: types.optional(RequestStateType, RequestState.NONE),
    syncState: types.optional(RequestStateType, RequestState.NONE),
    syncError: types.maybeNull(Error),
    rowMutationStates: types.frozen({}),
  })
  .views((self) => ({
    get activeDocument() {
      return getRoot(self).accountStore.loggedIn
        ? self.serverDocument
        : self.document;
    },
    get items() {
      return self.activeDocument.items || [];
    },
    get pendingDeletions() {
      return self.activeDocument.pendingDeletions || [];
    },
    get hasItems() {
      return self.items.length > 0;
    },
    get dirtyItems() {
      return self.items.filter((item) => item.dirty);
    },
    get hasDirtyItems() {
      return self.dirtyItems.length > 0 || self.pendingDeletions.length > 0;
    },
    get isSyncing() {
      return self.syncState === RequestState.LOADING;
    },
    get hasPendingRowMutations() {
      return Object.values(self.rowMutationStates).some(
        (state) => state === RequestState.LOADING
      );
    },
    getItemById(id) {
      return self.items.find((item) => item.extendedId === String(id));
    },
    isRowMutating(id) {
      return self.rowMutationStates[String(id)] === RequestState.LOADING;
    },
  }))
  .actions((self) => {
    let storageHandler = null;
    let scheduledSyncTimeout = null;
    let activeSyncPromise = null;
    let queuedSyncRequested = false;
    let authContextVersion = 0;
    let activeRowMutationPromises = {};
    let queuedRowMutations = {};
    const delayedSyncMs = 1200;

    const accountStore = () => getRoot(self).accountStore;
    const productStore = () => getRoot(self).productStore;

    const persistGuestDocument = (document) => {
      stringifyAndSetStorage(storageKeys.wishlistV2, document);
    };

    const setGuestDocument = (document) => {
      self.document = normalizeWishlistDocument(document, null);
      persistGuestDocument(self.document);
    };

    const setServerDocument = (document) => {
      self.serverDocument = normalizeWishlistDocument(
        document,
        getCurrentCustomerId()
      );
    };

    const getCurrentCustomerId = () => {
      return accountStore().loggedIn ? accountStore().customerId : null;
    };

    const createAuthContext = () => ({
      version: authContextVersion,
      customerId: getCurrentCustomerId(),
    });

    const isAuthContextCurrent = (context) => {
      return (
        context.version === authContextVersion &&
        context.customerId === getCurrentCustomerId()
      );
    };

    const getGuestDocument = () => {
      return normalizeWishlistDocument(self.document, null);
    };

    const getActiveDocument = () => {
      return accountStore().loggedIn
        ? normalizeWishlistDocument(self.serverDocument, getCurrentCustomerId())
        : getGuestDocument();
    };

    const updateActiveDocument = (document) => {
      if (accountStore().loggedIn) {
        setServerDocument(document);
      } else {
        setGuestDocument(document);
      }
    };

    const loadDocumentFromStorage = () => {
      const existingDocument = parseWishlistStorage(storageKeys.wishlistV2);

      if (isCurrentWishlistDocument(existingDocument)) {
        return normalizeWishlistDocument(existingDocument, null);
      }

      const legacyItems = parseWishlistStorage(storageKeys.wishlist);
      if (Array.isArray(legacyItems)) {
        stringifyAndSetStorage(storageKeys.wishlistLegacyBackup, legacyItems);
        const migratedDocument = createLegacyWishlistDocument(legacyItems);
        persistGuestDocument(migratedDocument);
        safeRemoveItem(storageKeys.wishlist);
        return normalizeWishlistDocument(migratedDocument, null);
      }

      return createEmptyWishlistDocument(null);
    };

    const cancelScheduledSync = () => {
      if (scheduledSyncTimeout) {
        clearTimeout(scheduledSyncTimeout);
        scheduledSyncTimeout = null;
      }
    };

    const setRowMutationState = (itemId, state) => {
      const nextStates = {
        ...self.rowMutationStates,
      };

      if (!state || state === RequestState.NONE) {
        delete nextStates[itemId];
      } else {
        nextStates[itemId] = state;
      }

      self.rowMutationStates = nextStates;
    };

    const updateServerItem = (itemId, updater) => {
      const activeDocument = getActiveDocument();
      const itemIndex = activeDocument.items.findIndex(
        (item) => item.extendedId === itemId
      );

      if (itemIndex === -1) {
        return;
      }

      const nextItems = [...activeDocument.items];
      const nextItem = updater(nextItems[itemIndex]);
      nextItems[itemIndex] = normalizeWishlistItem(nextItem, {
        dirty: nextItem.dirty,
        serverBacked: nextItem.serverBacked,
      });

      updateActiveDocument({
        ...activeDocument,
        items: nextItems,
      });
    };

    const queueRowMutation = (itemId, operation) => {
      queuedRowMutations[itemId] = operation;

      if (activeRowMutationPromises[itemId]) {
        return activeRowMutationPromises[itemId];
      }

      activeRowMutationPromises[itemId] = flow(function* processRowMutations() {
        while (queuedRowMutations[itemId]) {
          const operationToRun = queuedRowMutations[itemId];
          delete queuedRowMutations[itemId];
          const authContext = createAuthContext();

          setRowMutationState(itemId, RequestState.LOADING);

          try {
            if (operationToRun.type === 'save') {
              yield productStore().saveProduct({
                extendedId: operationToRun.item.extendedId,
                quantity: operationToRun.item.quantity,
              });

              if (!isAuthContextCurrent(authContext)) {
                return false;
              }

              const currentItem = self.getItemById(itemId);
              if (
                currentItem &&
                currentItem.quantity === operationToRun.item.quantity &&
                !queuedRowMutations[itemId]
              ) {
                updateServerItem(itemId, (item) => ({
                  ...item,
                  dirty: false,
                  serverBacked: true,
                }));
              }
            }

            if (operationToRun.type === 'delete') {
              yield productStore().deleteProduct({
                extendedId: itemId,
              });

              if (!isAuthContextCurrent(authContext)) {
                return false;
              }
            }
          } catch (error) {
            if (!isAuthContextCurrent(authContext)) {
              return false;
            }

            if (operationToRun.type === 'delete') {
              const currentItem = self.getItemById(itemId);
              if (!currentItem) {
                const activeDocument = getActiveDocument();
                const nextItems = [...activeDocument.items];
                nextItems.splice(
                  Math.min(operationToRun.index, nextItems.length),
                  0,
                  normalizeWishlistItem(operationToRun.item, {
                    dirty: false,
                    serverBacked: true,
                  })
                );

                updateActiveDocument({
                  ...activeDocument,
                  items: nextItems,
                });
              }
            }

            setRowMutationState(itemId, RequestState.ERROR);
            return false;
          }
        }

        setRowMutationState(itemId, RequestState.NONE);
        return true;
      })();

      const currentPromise = activeRowMutationPromises[itemId];
      currentPromise.finally(() => {
        if (activeRowMutationPromises[itemId] === currentPromise) {
          delete activeRowMutationPromises[itemId];
        }
      });

      return currentPromise;
    };

    const scheduleSync = ({ immediate = false } = {}) => {
      if (window.isSSR || !accountStore().loggedIn || !self.hasDirtyItems) {
        return;
      }

      if (self.syncState === RequestState.LOADING) {
        queuedSyncRequested = true;
        return;
      }

      cancelScheduledSync();
      if (immediate) {
        self.sync();
        return;
      }

      scheduledSyncTimeout = setTimeout(() => {
        scheduledSyncTimeout = null;
        self.sync();
      }, delayedSyncMs);
    };

    return {
      load() {
        if (window.isSSR) {
          return;
        }

        self.setLoading(true);
        setGuestDocument(loadDocumentFromStorage());
        self.setLoading(false);
      },
      setCustomerContext(customerId) {
        if (window.isSSR) {
          return;
        }

        authContextVersion += 1;
        queuedSyncRequested = false;
        activeRowMutationPromises = {};
        queuedRowMutations = {};
        self.rowMutationStates = {};

        if (!customerId) {
          cancelScheduledSync();
          setServerDocument(createEmptyWishlistDocument(null));
          self.syncError = null;
          self.syncState = RequestState.NONE;
          self.hydrateState = RequestState.NONE;
          return;
        }

        setServerDocument(createEmptyWishlistDocument(customerId));
      },
      hydrateFromServer: flow(function* hydrateFromServer() {
        if (window.isSSR || !accountStore().loggedIn) {
          return self.items;
        }

        if (self.hydrateState === RequestState.LOADING) {
          return self.items;
        }

        self.hydrateState = RequestState.LOADING;
        const authContext = createAuthContext();

        try {
          const serverItems = yield productStore().getProductList();
          if (!isAuthContextCurrent(authContext)) {
            return self.items;
          }

          const cleanServerItems = serverItems.map((item) =>
            normalizeWishlistItem(item, {
              dirty: false,
              serverBacked: true,
            })
          );

          const guestDocument = getGuestDocument();
          setServerDocument({
            version: self.serverDocument.version,
            customerId: getCurrentCustomerId(),
            items: mergeWishlistItems(guestDocument.items, cleanServerItems),
            pendingDeletions: [],
            lastHydratedAt: new Date().toISOString(),
          });

          self.hydrateState = RequestState.LOADED;
          scheduleSync();
          return self.items;
        } catch (error) {
          if (!isAuthContextCurrent(authContext)) {
            return self.items;
          }

          self.hydrateState = RequestState.ERROR;
          throw error;
        }
      }),
      addOrUpdateItem(item, options = {}) {
        if (window.isSSR) {
          return;
        }

        const activeDocument = getActiveDocument();
        const existingItem = self.getItemById(item.extendedId || item.id);
        const normalizedItem = normalizeWishlistItem(item, {
          dirty: options.dirty !== undefined ? options.dirty : true,
          serverBacked:
            options.serverBacked !== undefined
              ? options.serverBacked
              : !!(existingItem || {}).serverBacked,
        });
        const items = [...activeDocument.items];
        let nextItem = normalizedItem;

        if (existingItem) {
          const index = items.findIndex(
            (wishlistItem) =>
              wishlistItem.extendedId === normalizedItem.extendedId
          );
          nextItem = normalizeWishlistItem(
            {
              ...existingItem,
              ...normalizedItem,
            },
            {
              dirty: options.dirty !== undefined ? options.dirty : true,
              serverBacked:
                options.serverBacked !== undefined
                  ? options.serverBacked
                  : existingItem.serverBacked,
            }
          );
          items.splice(index, 1, nextItem);
        } else {
          items.push(nextItem);
        }

        updateActiveDocument({
          ...activeDocument,
          items,
        });

        if (accountStore().loggedIn) {
          if (options.sync !== false) {
            queueRowMutation(nextItem.extendedId, {
              type: 'save',
              item: nextItem,
            });
          }
        } else {
          scheduleSync();
        }

        return nextItem;
      },
      removeItem(itemOrId, options = {}) {
        if (window.isSSR) {
          return;
        }

        const activeDocument = getActiveDocument();
        const itemId = String(
          typeof itemOrId === 'string'
            ? itemOrId
            : itemOrId.extendedId || itemOrId.id
        );
        const existingItem = self.getItemById(itemId);

        if (!existingItem) {
          return;
        }

        updateActiveDocument({
          ...activeDocument,
          items: activeDocument.items.filter(
            (item) => item.extendedId !== itemId
          ),
          pendingDeletions: accountStore().loggedIn
            ? activeDocument.pendingDeletions
            : existingItem.serverBacked
            ? [...new Set([...activeDocument.pendingDeletions, itemId])]
            : activeDocument.pendingDeletions,
        });

        if (
          accountStore().loggedIn &&
          existingItem.serverBacked &&
          options.sync !== false
        ) {
          queueRowMutation(itemId, {
            type: 'delete',
            item: existingItem,
            index: activeDocument.items.findIndex(
              (item) => item.extendedId === itemId
            ),
          });
          return;
        }

        scheduleSync({ immediate: true });
      },
      sync() {
        cancelScheduledSync();

        if (!accountStore().loggedIn) {
          return Promise.resolve(false);
        }

        if (self.syncState === RequestState.LOADING) {
          queuedSyncRequested = true;
          return activeSyncPromise || Promise.resolve(false);
        }

        activeSyncPromise = flow(function* performSync() {
          while (true) {
            const activeDocument = getActiveDocument();
            const authContext = createAuthContext();

            self.syncError = null;
            self.syncState = RequestState.LOADING;

            try {
              for (const extendedId of activeDocument.pendingDeletions) {
                yield productStore().deleteProduct({ extendedId });
                if (!isAuthContextCurrent(authContext)) {
                  return false;
                }
              }

              for (const item of activeDocument.items.filter(
                (wishlistItem) => wishlistItem.dirty
              )) {
                yield productStore().saveProduct({
                  extendedId: item.extendedId,
                  quantity: item.quantity,
                });
                if (!isAuthContextCurrent(authContext)) {
                  return false;
                }
              }

              const serverItems = yield productStore().getProductList();
              if (!isAuthContextCurrent(authContext)) {
                return false;
              }

              const cleanItems = serverItems.map((item) =>
                normalizeWishlistItem(item, {
                  dirty: false,
                  serverBacked: true,
                })
              );
              const latestActiveDocument = getActiveDocument();
              const shouldContinue =
                queuedSyncRequested &&
                self.hasDirtyItems &&
                accountStore().loggedIn;

              setServerDocument({
                version: self.serverDocument.version,
                customerId: getCurrentCustomerId(),
                items: shouldContinue
                  ? mergeWishlistItems(latestActiveDocument.items, cleanItems)
                  : cleanItems,
                pendingDeletions: shouldContinue
                  ? latestActiveDocument.pendingDeletions.filter(
                      (extendedId) =>
                        !activeDocument.pendingDeletions.includes(extendedId)
                    )
                  : [],
                lastHydratedAt: new Date().toISOString(),
              });

              setGuestDocument(createEmptyWishlistDocument(null));
              safeRemoveItem(storageKeys.wishlistLegacyBackup);
              self.syncState = RequestState.LOADED;

              if (shouldContinue) {
                queuedSyncRequested = false;
                continue;
              }

              queuedSyncRequested = false;
              return true;
            } catch (error) {
              if (!isAuthContextCurrent(authContext)) {
                return false;
              }

              self.syncError = createErrorModel(error);
              self.syncState = RequestState.ERROR;
              return false;
            }
          }
        })();

        const currentSyncPromise = activeSyncPromise;
        currentSyncPromise.finally(() => {
          if (activeSyncPromise === currentSyncPromise) {
            activeSyncPromise = null;
          }
        });

        return currentSyncPromise;
      },
      resetSyncState() {
        cancelScheduledSync();
        queuedSyncRequested = false;
        self.syncError = null;
        self.syncState = RequestState.NONE;
      },
      startStorageListener() {
        if (window.isSSR || storageHandler) {
          return;
        }

        storageHandler = (event) => {
          if (
            event.key === storageKeys.wishlistV2 ||
            event.key === storageKeys.wishlist
          ) {
            self.load();
          }
        };

        window.addEventListener('storage', storageHandler);
      },
      stopStorageListener() {
        if (window.isSSR || !storageHandler) {
          return;
        }

        window.removeEventListener('storage', storageHandler);
        storageHandler = null;
        cancelScheduledSync();
      },
    };
  });

export default WishlistStore;
